import axios from 'axios';
import docReady from '../../../assets/js/docReady';
import { simOnlyDataOptions, simOnlyMinutesOptions } from './lib/variables';

class SimOnlyComparer {
    constructor(element) {
        this.element = element;
        this.filter = {
            min_data: 2000,
            min_minutes: 100,
            min_network_speed: null,
            group_provider_products: true,
            max_data: null,
            max_minutes: null,
            contract_duration: null,
            network_type: null,
            provider: null,
            focus: null,
            items_per_page: 20,
        };

        this.productContainer = null;
        this.noDataToggle = null;
        this.dataSliderMobile = {
            inputField: null,
            labelField: null,
            optionList: null,
        };
        this.dataSliderDesktop = {
            inputField: null,
            labelField: null,
            optionList: null,
        };

        this.minutesToggleMobile = null;
        this.minutesToggleDesktop = null;

        this.minutes = {
            unlimitedToggle: null,
            inputFields: null,
            inputField: null,
            labelField: null,
            optionList: null,
        };

        this.providerSelect = null;
        this.networkTypeFields = [];
        this.closeSpeedModalInputs = [];

        this.contractDurationInputs = null;

        this.focus = this.element.dataset.focus;
        // Attach event listeners

        this.speedModalToggle = null;
        this.filterModalInputElement = null;
    }

    bootstrap() {
        // Cache the base element once and use it to find other elements
        this.productContainer = this.element.querySelector('.products-container');
        this.contractDurationInputs = this.element.querySelectorAll('input[name="contract_duration"]');

        // Use getElementById for direct ID selectors
        this.dataSliderMobile = {
            inputField: document.getElementById('slider-input-data-mobile'),
            optionList: this.element.querySelectorAll('#datalist-data-mobile option'),
            labelField: document.getElementById('data-input-label-mobile'),
        };

        this.dataSliderDesktop = {
            inputField: document.getElementById('slider-input-data-desktop'),
            optionList: this.element.querySelectorAll('#datalist-data-desktop option'),
            labelField: document.getElementById('data-input-label-desktop'),
        };

        this.minutesToggleDesktop = document.getElementById('toggle-input-minutes-desktop');
        this.minutesToggleMobile = document.getElementById('toggle-input-minutes-mobile');

        this.minutes = {
            inputFields: this.element.querySelectorAll('input[name="min_minutes"]'),
        };

        this.providerSelect = this.element.querySelector('select[name="provider"]');
        this.networkTypeFields = this.element.querySelectorAll('input[name="network_type"]');
        this.speedModalToggle = document.getElementById('speed-info');
        this.closeSpeedModalInputs = this.element.querySelectorAll('.close-speed-modal-toggle');
        this.filterModalInputElement = document.getElementById('showFilterToggle');
        this.noDataToggle = document.getElementById('no-data');
    }

    attachEventListeners() {
        // Delegate events for input and change on data sliders
        this.element.addEventListener('input', (event) => {
            if (event.target === this.dataSliderMobile.inputField) {
                this.updateDataSelection(this.dataSliderMobile, event.target.value, false);
            } else if (event.target === this.dataSliderDesktop.inputField) {
                this.updateDataSelection(this.dataSliderDesktop, event.target.value, false);
            }
        });
        this.element.addEventListener('change', (event) => {
            if (event.target === this.dataSliderMobile.inputField) {
                this.updateDataSelection(this.dataSliderMobile, event.target.value);
                this.updateAltSlider(this.dataSliderDesktop, event.target.value);
            } else if (event.target === this.dataSliderDesktop.inputField) {
                this.updateDataSelection(this.dataSliderDesktop, event.target.value);
                this.updateAltSlider(this.dataSliderMobile, event.target.value);
            } else if (event.target.matches('#toggle-input-minutes-desktop, #toggle-input-minutes-mobile')) {
                const targetValue = event.target.checked ? '99999' : 0;
                this.updateMinutesSelection(targetValue);
                this.updateFilter('min_minutes', targetValue);
            } else if (event.target.matches('#speed-info')) {
                document.body.classList.toggle('no-scroll', event.target.checked);
                if (event.target.checked) {
                    this.logEvent('show-speed-modal', {});
                } else {
                    this.logEvent('hide-speed-modal', {});
                }
            } else if (event.target.name === 'upsell') {
                if (event.target.checked) {
                    this.logEvent('show-upsell', { upsell: event.target.dataset });
                } else {
                    this.logEvent('hide-upsell', { });
                }
            } else if (event.target === this.filterModalInputElement) {
                document.body.classList.toggle('no-scroll', event.target.checked);
                if (event.target.checked) {
                    this.logEvent('show-filterbar', {});
                } else {
                    this.logEvent('hide-filterbar', {});
                }
            } else if (event.target.name === 'pricing') {
                if (event.target.checked) {
                    this.logEvent('show-pricing', { product: event.target.value });
                } else {
                    this.logEvent('hide-pricing', {});
                }
            } else if (event.target.matches('input[type="checkbox"]')) {
                if (event.target.id === 'no-data') {
                    this.handleNoDataCheckboxChange(event.target.checked);
                } else if (event.target.id === 'no-minutes') {
                    this.handleNoMinutesCheckboxChange(event.target.checked);
                }
            } else if (event.target.matches('select, input[type="radio"]')) {
                this.handleSelectChange(event);
            }
        });

        // Event listener for closing speed modal
        this.closeSpeedModalInputs.forEach((input) => {
            input.addEventListener('click', () => {
                this.logEvent('hide-speed-modal', {});
                this.speedModalToggle.checked = false;
                this.filterModalInputElement.checked = false;
                document.body.classList.remove('no-scroll');
            });
        });
    }

    init() {
        this.bootstrap();
        this.attachEventListeners();
        this.setFilterFromData();
        const updated = this.overwriteUrlFilter(new URLSearchParams(window.location.search));
        this.logEvent('init', this.filter);
        if (updated) {
            this.renderProducts(false);
        }
        this.updateFilterElements();
    }

    setFilterFromData() {
        if (this.element.dataset.filter) {
            this.overwriteDataFilter(JSON.parse(this.element.dataset.filter));
        }
    }

    // Update filters/products from URL params
    overwriteUrlFilter(urlParams) {
        if (urlParams.size === 0) {
            return false;
        }
        this.logEvent('set-filter-from-url', { params: window.location.search });

        const dataOptions = Object.keys(simOnlyDataOptions());
        const minutesOptions = Object.keys(simOnlyMinutesOptions());

        let updated = false;

        const mbData = urlParams.get('mb');
        if (mbData && this.filter.min_data !== mbData && dataOptions.includes(mbData)) {
            this.filter.min_data = mbData;
            updated = true;
        }

        const minData = urlParams.get('min');
        if (minData && this.filter.min_minutes !== minData && minutesOptions.includes(minData)) {
            this.filter.min_minutes = minData;
            updated = true;
        }

        const contractDuration = urlParams.get('contractDuration');
        if (contractDuration && this.filter.contract_duration !== contractDuration) {
            this.filter.contract_duration = contractDuration;
            updated = true;
        }

        const networkType = urlParams.get('network_type');
        if (networkType && this.filter.network_type !== networkType) {
            this.filter.network_type = networkType;
            updated = true;
        }

        const provider = urlParams.get('provider') || urlParams.get('providers')?.split(',')[0];
        if (provider && this.filter.provider !== provider) {
            this.filter.provider = provider;
            updated = true;
        }

        return updated;
    }

    overwriteDataFilter(filter) {
        this.filter = { ...this.filter, ...filter };
    }

    updateFilterElements() {
        if (this.filter.min_data !== null && this.filter.min_data !== '') {
            const index = Object.keys(simOnlyDataOptions()).indexOf(this.filter.min_data.toString());
            this.updateAltSlider(this.dataSliderDesktop, index);
            this.updateAltSlider(this.dataSliderMobile, index);
        }
        if (this.filter.min_minutes !== null && this.filter.min_minutes !== '') {
            this.updateMinutesSelection(this.filter.min_minutes);
        }
        if (this.filter.contract_duration !== null && this.filter.contract_duration !== '') {
            this.updateContractDurationInput(this.filter.contract_duration);
        }
        if (this.filter.provider && this.filter.provider.length > 0) {
            this.providerSelect.value = this.filter.provider;
        }
        this.updateNetworkTypeInput(this.filter.network_type ? this.filter.network_type.toUpperCase() : '');
    }

    setLoading(loading) {
        if (!loading) {
            this.productContainer.classList.remove('is-loading');
        } else {
            this.productContainer.classList.add('is-loading');
        }
    }

    animateProducts() {
        this.element.classList.add('animate-products');
    }

    updateDataSelection(slider, value, updateFilter = true) {
        const selectedOption = slider.optionList[value];

        slider.labelField.innerHTML = selectedOption.label;

        if (this.focus === 'no-data-on') {
            this.updateFilter('max_data', value > 0 ? null : 1);
            this.noDataToggle.checked = false;
            this.focus = 'no-data-off';
        }
        if (updateFilter) {
            this.updateFilter('min_data', selectedOption.value);
        }
    }

    updateAltSlider(slider, value) {
        const selectedOption = slider.optionList[value];
        slider.labelField.innerHTML = selectedOption.label;
        slider.inputField.value = value;
    }

    updateMinutesSelection(targetValue) {
        this.minutesToggleDesktop.checked = targetValue === '99999';
        this.minutesToggleMobile.checked = targetValue === '99999';

        this.minutes.inputFields.forEach((input) => {
            if (input.value === targetValue) {
                input.checked = true;
            }
        });
    }

    updateNetworkTypeInput(targetValue) {
        this.networkTypeFields.forEach((input) => {
            if (input.value === targetValue) {
                input.checked = true;
            }
        });
    }

    updateContractDurationInput(targetValue) {
        this.contractDurationInputs.forEach((input) => {
            if (input.value === targetValue) {
                input.checked = true;
            }
        });
    }

    handleNoDataCheckboxChange(checked) {
        this.focus = checked ? 'no-data-on' : 'no-data-off';
        this.updateFilter('max_data', checked ? 1 : null);
        if (checked) {
            this.updateFilter('min_data', 0);
            this.updateAltSlider(this.dataSliderDesktop, 0);
            this.updateAltSlider(this.dataSliderMobile, 0);
        }
    }

    handleNoMinutesCheckboxChange(checked) {
        this.filter.max_minutes = checked ? 1 : null;
        this.updateFilter();
    }

    handleSelectChange(selectEvent) {
        if (selectEvent.target.name === 'provider') {
            this.updateFilter('provider', selectEvent.target.value !== '' ? [selectEvent.target.value] : []);
        } else {
            this.updateFilter(selectEvent.target.name, selectEvent.target.value);
        }
    }

    updateFilter(key, value) {
        this.logEvent('filter', { filter: key, filter_value: value });

        this.filter[key] = value;
        this.renderProducts();
    }

    cleanFilters() {
        const cleanedFilters = { ...this.filter };
        cleanedFilters.min_data = cleanedFilters.max_data === 1 ? 0 : cleanedFilters.min_data;
        cleanedFilters.min_minutes = cleanedFilters.max_minutes === 1 ? 0 : cleanedFilters.min_minutes;

        cleanedFilters.group_provider_products = !cleanedFilters.provider?.length > 0;

        if (!cleanedFilters.group_provider_products) {
            cleanedFilters.min_contract_duration = cleanedFilters.contract_duration;
        }
        // Remove all null values
        Object.keys(cleanedFilters)
            .forEach((key) => {
                if (cleanedFilters[key] === null) {
                    delete cleanedFilters[key];
                }
            });
        return cleanedFilters;
    }

    renderProducts(animate = true) {
        this.setLoading(true);
        this.logEvent('load-products', { filters: this.cleanFilters() });
        let apiUrl = '/ajax/sim-only/render-products';

        if (this.focus) {
            apiUrl = `/ajax/sim-only/render-products/focus?focus=${this.focus}`;
        }
        axios.get(apiUrl, {
            params: new URLSearchParams(this.cleanFilters()),
        })
            .then((response) => {
                this.productContainer.innerHTML = response.data;
            })
            .then(() => {
                this.setLoading(false);
                if (animate) {
                    this.animateProducts();
                }
            })
            .catch(() => {
                // Any error should be caught in the Controller and returned as a response
                // This is just in case
                this.setLoading(false);
                this.productContainer.innerHTML = '<div class="sim-only-product-card card no-results">    '
                    + '<h3 class="title">Sorry, er ging iets mis</h3>\n'
                    + '<p class="subtitle">Onze fout! Laad de pagina opnieuw en probeer het nog een keer.</p></div>';
            });
    }

    logEvent(event, eventData) {
        if (typeof window.bananas !== 'undefined') {
            window.bananas('event', `compare-sim-only.${event}`, eventData);
        }
    }
}

docReady(() => {
    const element = document.getElementById('sim-only-comparison');
    const simOnlyComparer = new SimOnlyComparer(element);
    simOnlyComparer.init();
});
